import { createApi } from '@reduxjs/toolkit/query/react';
import { RHYME_API_SERVICES } from 'type-declarations';
import { amplifyBaseQuery } from 'state-services';

const rhymeApiUrl = RHYME_API_SERVICES.RhymeApi;
const payerIntegrationId = 'ALL';

export const holidaySchedulesApi = createApi({
	reducerPath: '_holidaySchedulesApi',
	baseQuery: amplifyBaseQuery, // Responsible for handling requests
	tagTypes: ['holiday-schedules'], // Type(s) associated with a query which can be invalidated to trigger a query
	// Endpoints are used for configuring requests for the API
	endpoints: (build) => ({
		getHolidaySchedules: build.query({
			providesTags: ['holiday-schedules'],
			query: ({ selectedOrganizationId }: { selectedOrganizationId: string }) => ({
				method: 'GET',
				apiName: rhymeApiUrl,
				path: `organization/${selectedOrganizationId}/case-queue/payer-integration/${payerIntegrationId}`,
				useSnackbar: true,
				errorMsg: 'Fetching holiday schedules failed, please refresh the page',
			}),
		}),
		postHolidaySchedule: build.mutation({
			invalidatesTags: ['holiday-schedules'], // Tags invalidated by mutation, triggering automatic request
			query: ({
				selectedOrganizationId,
				body,
			}: {
				selectedOrganizationId: string;
				body: object;
			}) => ({
				method: 'POST',
				apiName: rhymeApiUrl,
				path: `organization/${selectedOrganizationId}/case-queue/payer-integration/${payerIntegrationId}`,
				options: { body, headers: { 'Content-Type': 'application/json' } },
				useSnackbar: true,
				successMsg: 'Holiday schedule created successfully',
				errorMsg: 'Holiday schedule creation failed',
			}),
		}),
		putHolidaySchedule: build.mutation({
			invalidatesTags: ['holiday-schedules'],
			query: ({
				selectedOrganizationId,
				id,
				body,
			}: {
				selectedOrganizationId: string;
				id: string;
				body: object;
			}) => ({
				method: 'PUT',
				apiName: rhymeApiUrl,
				path: `organization/${selectedOrganizationId}/case-queue/payer-integration/${payerIntegrationId}/id/${id}`,
				options: { body, headers: { 'Content-Type': 'application/json' } },
				useSnackbar: true,
				successMsg: 'Holiday schedule modified successfully',
				errorMsg: 'Holiday schedule modification failed',
			}),
		}),
		deleteHolidaySchedule: build.mutation({
			invalidatesTags: ['holiday-schedules'],
			query: ({ selectedOrganizationId, id }: { selectedOrganizationId: string; id: string }) => ({
				method: 'DELETE',
				apiName: rhymeApiUrl,
				path: `organization/${selectedOrganizationId}/case-queue/payer-integration/${payerIntegrationId}/id/${id}`,
				useSnackbar: true,
				successMsg: 'Holiday schedule removed successfully',
				errorMsg: 'Holiday schedule removal failed',
			}),
		}),
	}),
});

// Hooks for usage in functional components, auto-generated based on the defined endpoints
export const {
	useGetHolidaySchedulesQuery,
	usePostHolidayScheduleMutation,
	usePutHolidayScheduleMutation,
	useDeleteHolidayScheduleMutation,
} = holidaySchedulesApi;
